<template>
  <Teleport to="body">
    <div
      class="modalBg modalHeight z-[2200] w-full flex items-center justify-center mobOnly:px-12"
      @click.self="$emit('close')"
    >
      <div class="bg-lightest w-full max-w-[1200px] shadow-xl relative z-modalsContent flex flex-col desk:flex-row max-h-[90vh]">
        <!-- Close -->
        <button
          class="absolute top-8 right-8 desk:top-16 desk:right-16 p-8 text-icon-subtle hover:text-icon-base transition-all z-10"
          @click="$emit('close')"
        >
          <GlobalIcon
            name="close"
            size="small"
          />
        </button>

        <!-- Left: image / document preview -->
        <div class="desk:w-3/5 bg-lighter flex items-center justify-center min-h-[240px] desk:min-h-[500px] overflow-hidden">
          <template v-if="asset.mediaType === 'Images'">
            <nuxt-img
              v-if="isNorceUrl"
              :src="asset.url"
              provider="norce"
              preset="pdpimage"
              sizes="mob375:100vw desk:600px"
              loading="eager"
              class="w-full h-full object-contain max-h-[60vh] desk:max-h-[90vh]"
              :alt="asset.name"
            />
            <img
              v-else
              :src="asset.url"
              loading="eager"
              class="w-full h-full object-contain max-h-[60vh] desk:max-h-[90vh]"
              :alt="asset.name"
            >
          </template>
          <template v-else>
            <div class="flex flex-col items-center gap-16 p-32 text-text-subtle">
              <img
                :src="documentIcon"
                class="w-64 h-64 opacity-40"
                alt=""
              >
              <span class="type-sm text-center">{{ asset.name }}</span>
            </div>
          </template>
        </div>

        <!-- Right: metadata + actions -->
        <div class="desk:w-2/5 flex flex-col p-24 desk:p-32 overflow-y-auto">
          <div class="mb-24">
            <div class="type-headline-xl">
              {{ asset.productName }}
            </div>
          </div>

          <!-- Metadata fields -->
          <dl class="mb-32 space-y-12">
            <div>
              <dt class="type-xs text-text-subtle uppercase tracking-wide mb-2">
                {{ $t('mediaBank.preview.fields.articleNumber') }}
              </dt>
              <dd class="type-sm">
                {{ asset.articleNumber }}
              </dd>
            </div>
            <div v-if="asset.collection">
              <dt class="type-xs text-text-subtle uppercase tracking-wide mb-2">
                {{ $t('mediaBank.preview.fields.collection') }}
              </dt>
              <dd class="type-sm">
                {{ asset.collection }}
              </dd>
            </div>
            <div v-if="asset.productType">
              <dt class="type-xs text-text-subtle uppercase tracking-wide mb-2">
                {{ $t('mediaBank.filter.productType') }}
              </dt>
              <dd class="type-sm">
                {{ productTypeLabel }}
              </dd>
            </div>
            <div>
              <dt class="type-xs text-text-subtle uppercase tracking-wide mb-2">
                {{ asset.mediaType === 'Images' ? $t('mediaBank.preview.fields.imageType') : $t('mediaBank.preview.fields.documentType') }}
              </dt>
              <dd class="type-sm">
                {{ asset.mediaSetTitle }}
              </dd>
            </div>
          </dl>

          <!-- Download options -->
          <div class="mb-24">
            <div class="type-headline-sm mb-12">
              {{ $t('mediaBank.preview.downloadOptions') }}
            </div>

            <!-- PDF: Open in new tab -->
            <a
              v-if="isPdf"
              :href="asset.url"
              target="_blank"
              rel="noopener noreferrer"
              class="group w-full btn btn--secondary mb-8 justify-between !px-16"
            >
              <span class="flex items-center gap-8">
                <img
                  src="/icons/external-link-inv.svg"
                  class="w-18 h-18 group-hover:invert"
                  alt=""
                >
                <span>{{ $t('mediaBank.preview.openNewTab') }}</span>
              </span>
              <span class="type-xs opacity-70">PDF</span>
            </a>

            <!-- JPG download for images, or document download -->
            <button
              class="w-full btn mb-8 justify-between !px-16"
              :disabled="downloadingJpg"
              @click="handleDownloadJpg"
            >
              <span class="flex items-center gap-8">
                <LoadingIcon v-if="downloadingJpg" class="flex invert" />
                <img
                  v-else
                  src="/icons/download.svg"
                  class="w-18 h-18 invert"
                  alt=""
                >
                <span>{{ asset.mediaType === 'Images' ? $t('mediaBank.preview.download.jpg') : $t('mediaBank.preview.download.document') }}</span>
              </span>
              <span class="type-xs opacity-70 pl-6">
                {{ asset.mediaType === 'Images' ? $t('mediaBank.preview.download.digital') : asset.fileType.toUpperCase() }}
              </span>
            </button>

            <!-- TIFF download option - only shown for images when TIFF is available -->
            <button
              v-if="asset.mediaType === 'Images' && asset.originalTiffUrl"
              class="group w-full btn btn--secondary mb-8 justify-between !px-16"
              :disabled="downloadingTiff"
              @click="handleDownloadTiff"
            >
              <span class="flex items-center gap-8">
                <LoadingIcon v-if="downloadingTiff" class="flex" />
                <img
                  v-else
                  src="/icons/download.svg"
                  class="w-18 h-18 group-hover:invert"
                  alt=""
                >
                <span>{{ $t('mediaBank.preview.download.tiff') }}</span>
              </span>
              <span class="type-xs opacity-70 pl-6">{{ $t('mediaBank.preview.download.print') }}</span>
            </button>
          </div>

          <!-- Select toggle -->
          <button
            class="btn btn--secondary w-full justify-center gap-8"
            @click="$emit('toggleFavorite', asset)"
          >
            <img
              :src="isFavorite ? '/icons/check.svg' : '/icons/plus.svg'"
              class="w-16 h-16"
              alt=""
            >
            {{ isFavorite ? $t('mediaBank.preview.removeSelected') : $t('mediaBank.preview.addSelected') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useMediaBankStore } from '~/store/mediaBank';
import { useGlobalContentStore } from '~/store/globalContent';
import type { MediaAsset } from '#constants/types/mediaBank';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';

defineOptions({
  inheritAttrs: false,
});

const { $t } = useNuxtApp();
const mediaBankStore = useMediaBankStore();
const globalContentStore = useGlobalContentStore();

const props = defineProps<{
  asset: MediaAsset;
  downloadingUrl?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'download', asset: MediaAsset, url?: string): void;
  (e: 'toggleFavorite', asset: MediaAsset): void;
}>();

const isFavorite = computed(() => mediaBankStore.isFavorite(props.asset.id));
const productTypeLabel = computed(() =>
  props.asset.productType ? globalContentStore.getFilterOption('categoryFilters.' + props.asset.productType).name : ''
);
const isPdf = computed(() => props.asset.mimeType === 'application/pdf' || props.asset.fileType === '.pdf');
const isZip = computed(() => props.asset.mimeType.includes('zip') || props.asset.fileType === '.zip');
const documentIcon = computed(() => (isZip.value ? '/icons/file-zip.svg' : '/icons/file-pdf.svg'));
const isNorceUrl = computed(() => props.asset.url.includes('cdn-norce.tech'));

// Check if specific URL is currently downloading
const downloadingJpg = computed(() => {
  const jpgUrl = props.asset.originalJpgUrl || props.asset.url;
  return props.downloadingUrl === jpgUrl;
});
const downloadingTiff = computed(() => props.downloadingUrl === props.asset.originalTiffUrl);

const handleDownloadJpg = () => {
  const url = props.asset.originalJpgUrl || props.asset.url;
  emit('download', props.asset, url);
};

const handleDownloadTiff = () => {
  if (props.asset.originalTiffUrl) {
    emit('download', props.asset, props.asset.originalTiffUrl);
  }
};
</script>
